import {
  MapPin,
  User,
  LucideIcon,
  UserPlus,
  Plus,
  Shield,
  Building2,
  Search,
  Stethoscope,
  FileCode,
} from "lucide-react";
import { useState, useMemo, useCallback } from "react";

import { Badge } from "@/src/components/shadcn/ui/badge";
import { useCPTSearch } from "@/src/hooks/use-cpt-search";
import { useDebounce } from "@/src/hooks/use-debounce";
import { useGeocodingSearch } from "@/src/hooks/use-geocoding-search";
import { useICD10Search } from "@/src/hooks/use-icd10-search";
import { useNPISearch } from "@/src/hooks/use-npi-search";
import { searchCoverages } from "@/src/lib/coverage-utils";
import { usePatientSearch } from "@/src/lib/data-fetchers/patients-fetchers";
import { matchLocationByAddress, searchLocationsByText } from "@/src/lib/location-search";
import { patientsSearch } from "@/src/lib/patient";
import { matchPayersInList, searchPayer } from "@/src/lib/payer-search";
import { providersSearch } from "@/src/lib/provider-search";
import { Coverage, getCoverageDisplayName } from "@/src/models/coverage";
import { CPT, getCPTDisplayName } from "@/src/models/cpt";
import { ICD10, getICD10DisplayName } from "@/src/models/icd10";
import { Location, getLocationDisplayName } from "@/src/models/location";
import { convertNPIResultToProvider } from "@/src/models/npi-registry";
import { Patient, getPatientDisplayName } from "@/src/models/patient";
import { Payer, getPayerDisplayName } from "@/src/models/payer";
import { Practitioner, getProviderDisplayName } from "@/src/models/practitioner";

// Define the allowed entity types for EntityMatcher
export type EntityMatcherType = Patient | Practitioner | Location | Coverage | Payer | ICD10 | CPT;
export type EntityType = "patient" | "practitioner" | "location" | "coverage" | "payer" | "icd10" | "cpt";
export type topInsurancePayersList = string[];
export type EntityWeightingType = topInsurancePayersList; // we can add more types here if needed

export interface ExternalDataSource<T extends EntityMatcherType> {
  search: (query: string) => void;
  isLoading: boolean;
  error: string | null;
  results: T[];
  shouldShow: (query: string) => boolean;
  getDisplayName: (item: T) => string;
  getIcon: (item: T) => LucideIcon;
  groupTitle: string;
}

export interface EntityMatcherConfig<T extends EntityMatcherType> {
  displayConfig: {
    getDisplayName: (entity: T) => string;
    entityIcon: LucideIcon;
    createNewIcon: LucideIcon;
    entityListTitle: string;
    createNewTitle: string;
    placeholder: string;
    renderBadge?: (entity: T) => React.ReactNode;
  };
  externalDataSources: ExternalDataSource<T>[];
  search: (entities: T[], query: Partial<T> | string | null, weighting?: EntityWeightingType) => T[];
  generateInitialSearchQuery?: (entity: T) => string;
}

// Centralized configuration object for static config
const ENTITY_MATCHER_STATIC_CONFIGS = {
  patient: {
    displayConfig: {
      getDisplayName: getPatientDisplayName,
      entityIcon: User,
      createNewIcon: UserPlus,
      entityListTitle: "Patients",
      createNewTitle: "Create new patient",
      placeholder: "Search patients...",
    },
    search: (patients: Patient[], query: Partial<Patient> | string | null) => patientsSearch(patients, query),
  },
  practitioner: {
    displayConfig: {
      getDisplayName: getProviderDisplayName,
      entityIcon: User,
      createNewIcon: UserPlus,
      entityListTitle: "Providers",
      createNewTitle: "Create new provider",
      placeholder: "Search providers or NPI registry...",
    },
    search: (practitioners: Practitioner[], query: Partial<Practitioner> | string | null) =>
      providersSearch(practitioners, query),
    generateInitialSearchQuery: (practitioner: Practitioner) => {
      if (practitioner.npiNumber) {
        return practitioner.npiNumber;
      }

      const name = [practitioner.givenName, practitioner.familyName].filter(Boolean).join(" ");

      return name || "";
    },
  },
  location: {
    displayConfig: {
      getDisplayName: getLocationDisplayName,
      entityIcon: MapPin,
      createNewIcon: Plus,
      entityListTitle: "Locations",
      createNewTitle: "Create new location",
      placeholder: "Search locations or addresses...",
    },
    search: (locations: Location[], query: Partial<Location> | string | null) => {
      // Free-text typeahead vs. structured address matching are different algorithms.
      if (typeof query === "string") {
        return searchLocationsByText(locations, query);
      }

      return matchLocationByAddress(locations, query);
    },
    generateInitialSearchQuery: (location: Location) => {
      const parts = [
        location.address?.line1,
        location.address?.city,
        location.address?.state,
        location.address?.postalCode,
      ].filter(Boolean);

      return parts.join(", ");
    },
  },
  coverage: {
    displayConfig: {
      getDisplayName: getCoverageDisplayName,
      entityIcon: Shield,
      createNewIcon: Plus,
      entityListTitle: "Coverages",
      createNewTitle: "Create new coverage",
      placeholder: "Search insurance coverage...",
      renderBadge: (coverage: Coverage) => {
        if (coverage.order === 1) {
          return (
            <Badge variant="secondary" className="text-xs rounded-full bg-background text-foreground border-foreground">
              Primary
            </Badge>
          );
        }

        return null;
      },
    },
    search: (coverages: Coverage[], query: Partial<Coverage> | string | null) => searchCoverages(coverages, query),
  },
  payer: {
    displayConfig: {
      getDisplayName: getPayerDisplayName,
      entityIcon: Building2,
      createNewIcon: Search,
      entityListTitle: "Payers",
      createNewTitle: "Search for payor",
      placeholder: "Search for payor...",
    },
    search: (payers: Payer[], query: Partial<Payer> | string | null, weighting?: EntityWeightingType) => {
      if (typeof query === "string") {
        return searchPayer(payers, query, weighting);
      }

      if (query === null) {
        return searchPayer(payers, "", weighting);
      }

      return matchPayersInList(payers, query);
    },
  },
  icd10: {
    displayConfig: {
      getDisplayName: getICD10DisplayName,
      entityIcon: Stethoscope,
      createNewIcon: Search,
      entityListTitle: "ICD-10 Codes",
      createNewTitle: "Search ICD-10 codes",
      placeholder: "Search ICD-10 codes...",
    },
    search: (_icd10s: ICD10[], _query: Partial<ICD10> | string | null) => {
      // No-op: ICD-10 search is handled server-side
      return [];
    },
  },
  cpt: {
    displayConfig: {
      getDisplayName: getCPTDisplayName,
      entityIcon: FileCode,
      createNewIcon: Search,
      entityListTitle: "CPT Codes",
      createNewTitle: "Search CPT codes",
      placeholder: "Search CPT codes...",
    },
    search: (_cpts: CPT[], _query: Partial<CPT> | string | null) => {
      return [];
    },
  },
} as const;

// Hook to get complete entity matcher configuration including external data sources
export const useEntityMatcherConfig = (type: EntityType): EntityMatcherConfig<EntityMatcherType> => {
  const npiSource = useNPISource();
  const geocodingSource = useGeocodingSource();
  const icd10Source = useICD10Source();
  const cptSource = useCPTSource();
  const patientSource = usePatientSearchSource();

  return useMemo(() => {
    let config;

    switch (type) {
      case "practitioner":
        config = {
          ...ENTITY_MATCHER_STATIC_CONFIGS.practitioner,
          externalDataSources: [npiSource],
        };
        break;
      case "location":
        config = {
          ...ENTITY_MATCHER_STATIC_CONFIGS.location,
          externalDataSources: [geocodingSource],
        };
        break;
      case "patient":
        config = {
          ...ENTITY_MATCHER_STATIC_CONFIGS.patient,
          externalDataSources: [patientSource],
        };
        break;
      case "coverage":
        config = {
          ...ENTITY_MATCHER_STATIC_CONFIGS.coverage,
          externalDataSources: [],
        };
        break;
      case "payer":
        config = {
          ...ENTITY_MATCHER_STATIC_CONFIGS.payer,
          externalDataSources: [],
        };
        break;
      case "icd10":
        config = {
          ...ENTITY_MATCHER_STATIC_CONFIGS.icd10,
          externalDataSources: [icd10Source],
        };
        break;
      case "cpt":
        config = {
          ...ENTITY_MATCHER_STATIC_CONFIGS.cpt,
          externalDataSources: [cptSource],
        };
        break;
      default:
        throw new Error(`Unknown entity type: ${type}`);
    }

    return config as EntityMatcherConfig<EntityMatcherType>;
  }, [type, npiSource, geocodingSource, icd10Source, cptSource, patientSource]);
};

export const useGeocodingSource = (): ExternalDataSource<Location> => {
  const [currentSearch, setCurrentSearch] = useState("");
  const { results: geocodeResults, loading: geocodeLoading, error: geocodeError } = useGeocodingSearch(currentSearch);

  const search = useCallback((query: string) => {
    setCurrentSearch(query);
  }, []);

  const shouldShow = useCallback((query: string) => query.length >= 3, []);

  return useMemo(
    () => ({
      search,
      isLoading: geocodeLoading,
      error: geocodeError,
      results: geocodeResults,
      shouldShow,
      getDisplayName: getLocationDisplayName,
      getIcon: (_item: Location) => MapPin,
      groupTitle: "External addresses",
    }),
    [search, geocodeLoading, geocodeError, geocodeResults, shouldShow]
  );
};

export const useNPISource = (): ExternalDataSource<Practitioner> => {
  const [currentSearch, setCurrentSearch] = useState("");
  const { results: npiResults, loading: npiLoading, error: npiError } = useNPISearch(currentSearch);

  const results = useMemo(() => npiResults.map(convertNPIResultToProvider), [npiResults]);

  const search = useCallback((query: string) => {
    setCurrentSearch(query);
  }, []);

  const shouldShow = useCallback((query: string) => query.length >= 3, []);

  return useMemo(
    () => ({
      search,
      isLoading: npiLoading,
      error: npiError,
      results,
      shouldShow,
      getDisplayName: getProviderDisplayName,
      getIcon: (_item: Practitioner) => User,
      groupTitle: "NPI Registry Providers",
    }),
    [search, npiLoading, npiError, results, shouldShow]
  );
};

/**
 * External data source that resolves patient matches via the server-side
 * `/api/patients/search` endpoint. Lets the EntityMatcher search the full set
 * of patients without ever loading them into the client up-front.
 */
export const usePatientSearchSource = (): ExternalDataSource<Patient> => {
  const [currentSearch, setCurrentSearch] = useState("");
  const debouncedSearch = useDebounce(currentSearch, 200);
  const { patients, isFetching, isLoading, error } = usePatientSearch(debouncedSearch, {
    enabled: debouncedSearch.length >= 2,
  });

  const search = useCallback((query: string) => {
    setCurrentSearch(query);
  }, []);

  const shouldShow = useCallback((query: string) => query.trim().length >= 2, []);

  const errorMessage = error instanceof Error ? error.message : null;

  return useMemo(
    () => ({
      search,
      isLoading: isLoading || isFetching,
      error: errorMessage,
      results: patients,
      shouldShow,
      getDisplayName: getPatientDisplayName,
      getIcon: (_item: Patient) => User,
      groupTitle: "Patients",
    }),
    [search, isLoading, isFetching, errorMessage, patients, shouldShow]
  );
};

export const useICD10Source = (): ExternalDataSource<ICD10> => {
  const [currentSearch, setCurrentSearch] = useState("");
  const { results: icd10Results, loading: icd10Loading, error: icd10Error } = useICD10Search(currentSearch);

  const search = useCallback((query: string) => {
    setCurrentSearch(query);
  }, []);

  const shouldShow = useCallback((query: string) => query.trim().length >= 2, []);

  return useMemo(
    () => ({
      search,
      isLoading: icd10Loading,
      error: icd10Error,
      results: icd10Results,
      shouldShow,
      getDisplayName: getICD10DisplayName,
      getIcon: (_icd10: ICD10) => Stethoscope,
      groupTitle: "ICD-10 Codes",
    }),
    [search, icd10Loading, icd10Error, icd10Results, shouldShow]
  );
};

export const useCPTSource = (): ExternalDataSource<CPT> => {
  const [currentSearch, setCurrentSearch] = useState("");
  const { results: cptResults, loading: cptLoading, error: cptError } = useCPTSearch(currentSearch);

  const search = useCallback((query: string) => {
    setCurrentSearch(query);
  }, []);

  const shouldShow = useCallback((query: string) => query.trim().length >= 2, []);

  return useMemo(
    () => ({
      search,
      isLoading: cptLoading,
      error: cptError,
      results: cptResults,
      shouldShow,
      getDisplayName: getCPTDisplayName,
      getIcon: (_cpt: CPT) => FileCode,
      groupTitle: "CPT Codes",
    }),
    [search, cptLoading, cptError, cptResults, shouldShow]
  );
};
